import { useEffect, useState } from 'react';

import { useDebounce, useElementKeyDown, useMounted } from '@playbooks/hooks';
import { Icon } from '@playbooks/ui/components/icon';
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from '@playbooks/ui/components/input-group';
import { cn } from '@playbooks/ui/lib/utils';

type iSearchForm = {
	id?: string;
	'aria-labelledby'?: string;
	ref?: any;
	delay?: number;
	length?: number;
	prevIcon?: string;
	placeholder?: string;
	loading?: boolean;
	disabled?: boolean;
	query: string;
	setQuery: any;
	onClick?: () => any;
	onBlur?: () => any;
	onFocus?: () => any;
	onClear?: () => any;
	elements?: any;
	size?: 'xs' | 'sm' | 'default' | 'lg';
	className?: string;
};
const SearchForm = ({
	id = 'search_form',
	'aria-labelledby': ariaLabelledby,
	ref,
	delay = 300,
	length = 3,
	prevIcon = 'magnifying-glass',
	placeholder,
	query,
	setQuery,
	loading,
	disabled = false,
	onBlur = null,
	onClick,
	onFocus,
	onClear: onClearCallback,
	elements,
	size,
	className = '',
}: iSearchForm) => {
	const [value, setValue] = useState(query);

	// Hooks
	const debouncedValue = useDebounce(value, delay);
	useMounted(() => {
		setValue(query);
	}, [query]);
	useMounted(() => {
		if (value.length === 0) setQuery(value);
	}, [value]);
	useEffect(() => {
		if (value.length >= length) setQuery(debouncedValue);
	}, [debouncedValue]);
	useElementKeyDown(
		ref?.current,
		e => {
			if (e.target.id !== id) return;
			if (e.keyCode === 27) {
				e.preventDefault();
				onClear();
			}
		},
		[],
	);

	// Methods
	const onChange = e => setValue(e.target.value);
	const onClear = () => {
		setValue('');
		setQuery('');
		onClearCallback?.();
		const input = document.getElementById(id) as HTMLInputElement;
		if (input) input.value = '';
	};

	// Theme
	const rootClassName = cn('overflow-hidden', className);

	// Render
	return (
		<InputGroup size={size} data-disabled={disabled || undefined} className={rootClassName}>
			{elements?.inputPrepend ? (
				elements?.inputPrepend
			) : (
				<InputGroupAddon>
					<Icon type='far' icon={prevIcon} aria-hidden />
				</InputGroupAddon>
			)}
			<InputGroupInput
				id={id}
				aria-labelledby={ariaLabelledby}
				ref={ref}
				value={value}
				disabled={disabled}
				placeholder={placeholder}
				onChange={onChange}
				onClick={onClick}
				onBlur={onBlur}
				onFocus={onFocus}
			/>
			{elements?.inputAppend ? (
				elements?.inputAppend
			) : (
				<InputGroupAddon align='inline-end'>
					{value.length >= length && (
						<InputGroupButton isLoading={loading} aria-label='Clear search' onClick={onClear}>
							<Icon data-icon='inline-start' type='far' icon='xmark' className='text-xs' aria-hidden />
						</InputGroupButton>
					)}
				</InputGroupAddon>
			)}
		</InputGroup>
	);
};
export { SearchForm };
